import { useState, type FormEvent } from 'react';
import { integrationsApi, type IntegrationStatus } from '@/api/client';
import { useAsync } from '@/hooks';
import { Layout } from '@/components/Layout';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { DataTable } from '@/components/DataTable';
import { StatusBadge } from '@/components/StatusBadge';
import { adminNavItems } from '@/pages/navItems';
import { useToast } from '@/components/Feedback';
import { formatDateTime, humanize } from '@/lib/format';
import { FlashMessage, errorText, useFlash } from '@/components/FlashMessage';

const PROVIDERS: Array<{
  id: 'xero' | 'google_maps' | 'mapbox' | 'resend' | 'storage' | 'firebase';
  title: string;
  description: string;
  fields: Array<{ key: string; label: string; type?: string; placeholder?: string; hint?: string }>;
}> = [
  {
    id: 'xero',
    title: 'Xero',
    description: 'Push draft invoices, crew payouts and bills. Secrets are encrypted and never shown again after saving.',
    fields: [
      { key: 'clientId', label: 'Client ID', placeholder: 'Xero OAuth client id' },
      { key: 'clientSecret', label: 'Client secret', type: 'password', placeholder: '••••••••' },
      { key: 'tenantId', label: 'Tenant ID (optional)', placeholder: 'After first connect' },
    ],
  },
  {
    id: 'google_maps',
    title: 'Google Maps',
    description:
      'Geocode and address autocomplete via Google. Credentials stay saved even when Mapbox is the active provider — switch below.',
    fields: [
      { key: 'apiKey', label: 'API key', type: 'password', placeholder: 'AIza…' },
    ],
  },
  {
    id: 'mapbox',
    title: 'Mapbox',
    description:
      'Geocode, search, static images and lane distance via Mapbox. Use a public (pk.) token for the browser and a secret (sk.) token for server geocoding.',
    fields: [
      { key: 'publicToken', label: 'Public token (pk.)', type: 'password', placeholder: 'pk.…' },
      { key: 'secretToken', label: 'Secret token (sk.)', type: 'password', placeholder: 'sk.…' },
      {
        key: 'styleUrl',
        label: 'Style URL (optional)',
        placeholder: 'mapbox://styles/mapbox/streets-v12',
      },
    ],
  },
  {
    id: 'resend',
    title: 'Email (Resend)',
    description:
      'Transactional email via Resend API — portal invitations, notifications and password resets.',
    fields: [
      { key: 'apiKey', label: 'API key', type: 'password', placeholder: 're_…' },
      { key: 'from', label: 'From address', placeholder: 'ops@11eleven.ph', hint: 'Must be a verified domain/sender in Resend' },
    ],
  },
  {
    id: 'storage',
    title: 'Object storage (Cloudflare R2)',
    description:
      'Where proof-of-delivery PDFs, signatures, delivery photos and receipts are kept. Create an R2 bucket and an API token with Object Read & Write on that bucket, then enter the details here. Until this is set up, files are saved on the server itself.',
    fields: [
      { key: 'accountId', label: 'Account ID', placeholder: 'e.g. 3f2c…a91b', hint: 'Shown on the R2 overview page in Cloudflare' },
      { key: 'bucket', label: 'Bucket', placeholder: 'dispatchdash-files' },
      { key: 'accessKeyId', label: 'Access key ID' },
      { key: 'secretAccessKey', label: 'Secret access key', type: 'password' },
      {
        key: 'endpoint',
        label: 'Custom endpoint (optional)',
        placeholder: 'Only for other S3-compatible storage',
        hint: 'Leave blank for Cloudflare R2. Use e.g. https://sgp1.digitaloceanspaces.com for DigitalOcean Spaces.',
      },
    ],
  },
  {
    id: 'firebase',
    title: 'Push notifications (Firebase)',
    description:
      'Sends notifications to the crew Android app — new trips, changes and cancellations. In Firebase, open Project settings → Service accounts, click "Generate new private key", then upload or paste that key file here. It is encrypted and never shown again.',
    fields: [],
  },
];

function statusLabel(s?: IntegrationStatus) {
  if (!s || !s.configured) return 'Not configured';
  return s.status === 'verified' ? 'Connected' : 'Configured';
}

export function AdminIntegrations() {
  const { data: statuses, loading, error, reload } = useAsync(() => integrationsApi.list(), []);
  const [active, setActive] = useState<(typeof PROVIDERS)[number]['id']>('xero');
  const [form, setForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const { flash, ok: showOk, error: showError, warn: showWarn, clear: clearFlash } = useFlash();
  const toast = useToast();
  const [testTo, setTestTo] = useState('');
  const [testingStorage, setTestingStorage] = useState(false);

  const byProvider = Object.fromEntries((statuses ?? []).map((s) => [s.provider, s]));
  const provider = PROVIDERS.find((p) => p.id === active)!;

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    clearFlash();
    try {
      const credentials = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v.trim().length > 0),
      );
      if (!Object.keys(credentials).length) {
        showError('Enter at least one field to save');
        return;
      }
      await integrationsApi.saveCredentials(active, credentials);
      setForm({});
      showOk(`${provider.title} settings saved securely`);
      toast(`${provider.title} settings saved`);
      reload();
    } catch (err) {
      showError(errorText(err, `Could not save the ${provider.title} settings.`));
    } finally {
      setSaving(false);
    }
  };

  const testEmail = async () => {
    setTesting(true);
    clearFlash();
    try {
      const result = await integrationsApi.testEmail(testTo);
      if (result.mode === 'stub') {
        showWarn(
          `No email was sent because email isn't connected yet. Save your Resend API key above, then try again.`,
        );
      } else {
        showOk(`Test email sent to ${testTo}`);
      }
    } catch (err) {
      showError(errorText(err, 'The test email could not be sent.'));
    } finally {
      setTesting(false);
    }
  };

  const testStorage = async () => {
    setTestingStorage(true);
    clearFlash();
    try {
      const result = await integrationsApi.testStorage();
      if (result.success) {
        showOk(result.message);
        toast('Storage connection works');
      } else if (result.mode === 'local') {
        showWarn(result.message);
      } else {
        showError(result.message);
      }
      reload();
    } catch (err) {
      showError(errorText(err, 'The storage connection could not be tested.'));
    } finally {
      setTestingStorage(false);
    }
  };

  const connectXero = async () => {
    clearFlash();
    try {
      const { url, mode } = await integrationsApi.xeroOAuthUrl();
      if (!url) {
        showWarn('Save a Xero client ID first, then connect.');
        return;
      }
      if (mode === 'stub') {
        showWarn('Xero is not connected yet. Save your Xero credentials above first.');
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      showError(errorText(err, 'Could not open the Xero sign-in page.'));
    }
  };

  return (
    <Layout navItems={adminNavItems}>
      <div className="page">
        <div className="page-header">
          <h1>Integrations</h1>
          <p className="text-muted">
            Configure Xero, Google Maps, Mapbox, Resend, file storage (Cloudflare R2) and crew app push
            notifications (Firebase). Secrets are encrypted at
            rest and never re-displayed. Use the maps provider switch to choose which geocoder is
            active system-wide.
          </p>
        </div>

        <ActiveMapsProviderCard />

        {loading ? (
          <div className="loading-center"><div className="spinner" /></div>
        ) : error ? (
          <p className="text-urgent">{error}</p>
        ) : (
          <div className="integrations-layout">
            <aside className="provider-list">
              {PROVIDERS.map((p) => {
                const st = byProvider[p.id];
                return (
                  <button
                    key={p.id}
                    type="button"
                    className={`provider-item ${active === p.id ? 'active' : ''}`}
                    onClick={() => {
                      setActive(p.id);
                      setForm({});
                      clearFlash();
                    }}
                  >
                    <strong>{p.title}</strong>
                    <span className={st?.configured ? 'ok' : 'muted'}>{statusLabel(st)}</span>
                  </button>
                );
              })}
            </aside>

            <section className="provider-panel">
              <h2>{provider.title}</h2>
              <p className="text-muted">{provider.description}</p>
              <p className="status-line">
                Status: <strong>{statusLabel(byProvider[active])}</strong>
                {byProvider[active]?.lastVerifiedAt
                  ? ` · last verified ${formatDateTime(byProvider[active].lastVerifiedAt!)}`
                  : ''}
              </p>

              {active === 'storage' && <StorageWhere status={byProvider.storage} />}

              {active === 'firebase' ? (
                <FirebasePanel status={byProvider.firebase} onSaved={reload} />
              ) : (
              <form className="cred-form" onSubmit={save}>
                {provider.fields.map((f) => (
                  <Input
                    key={f.key}
                    label={f.label}
                    type={f.type ?? 'text'}
                    value={form[f.key] ?? ''}
                    placeholder={f.placeholder}
                    hint={f.hint}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    autoComplete="off"
                  />
                ))}
                <div className="row">
                  <Button type="submit" loading={saving}>Save credentials</Button>
                  {active === 'xero' && (
                    <Button type="button" variant="secondary" onClick={connectXero}>
                      Connect with Xero
                    </Button>
                  )}
                  {active === 'storage' && (
                    <Button type="button" variant="secondary" loading={testingStorage} onClick={testStorage}>
                      Test connection
                    </Button>
                  )}
                </div>
              </form>
              )}

              {active === 'resend' && (
                <div className="test-block">
                  <h3>Send test email</h3>
                  <Input
                    label="Recipient"
                    type="email"
                    value={testTo}
                    onChange={(e) => setTestTo(e.target.value)}
                    placeholder="you@11eleven.ph"
                  />
                  <Button type="button" variant="secondary" loading={testing} onClick={testEmail} disabled={!testTo}>
                    Send test
                  </Button>
                </div>
              )}

              <FlashMessage flash={flash} />

              {active === 'xero' && <XeroRetryQueue />}
              {(active === 'google_maps' || active === 'mapbox') && <MapsUsagePanel />}
            </section>
          </div>
        )}

        <style>{`
          .integrations-layout {
            display: grid;
            grid-template-columns: 240px 1fr;
            gap: var(--space-lg);
            align-items: start;
          }
          @media (max-width: 800px) {
            .integrations-layout { grid-template-columns: 1fr; }
          }
          .provider-list {
            display: flex;
            flex-direction: column;
            gap: 4px;
            background: var(--color-surface);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            padding: var(--space-sm);
          }
          .provider-item {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 2px;
            padding: var(--space-md);
            border: none;
            border-radius: var(--radius-sm);
            background: transparent;
            cursor: pointer;
            text-align: left;
          }
          .provider-item:hover { background: var(--color-sand); }
          .provider-item.active {
            background: var(--color-forest);
            color: var(--color-sand);
          }
          .provider-item strong { font-size: 0.9375rem; }
          .provider-item span { font-size: 0.75rem; opacity: 0.8; }
          .provider-item .ok { color: inherit; }
          .provider-panel {
            background: var(--color-surface);
            border: 1px solid var(--color-border);
            border-radius: var(--radius-md);
            padding: var(--space-xl);
          }
          .provider-panel h2 { margin-bottom: var(--space-sm); color: var(--color-forest); }
          .status-line { font-size: 0.875rem; margin: var(--space-md) 0 var(--space-lg); }
          .cred-form {
            display: flex;
            flex-direction: column;
            gap: var(--space-md);
            max-width: 480px;
          }
          .test-block {
            margin-top: var(--space-xl);
            padding-top: var(--space-lg);
            border-top: 1px solid var(--color-border);
            max-width: 480px;
            display: flex;
            flex-direction: column;
            gap: var(--space-md);
          }
          .test-block h3 { font-size: 1rem; }
          .row { display: flex; gap: var(--space-sm); flex-wrap: wrap; }
        `}</style>
      </div>
    </Layout>
  );
}

function ActiveMapsProviderCard() {
  const { data, loading, reload } = useAsync(() => integrationsApi.getMapsProvider(), []);
  const [saving, setSaving] = useState(false);
  const { flash, ok: showOk, error: showError, clear: clearFlash } = useFlash();
  const active = data?.provider ?? 'google';

  const select = async (provider: 'google' | 'mapbox' | 'manual') => {
    if (provider === active) return;
    setSaving(true);
    clearFlash();
    try {
      await integrationsApi.setMapsProvider(provider);
      showOk(
        provider === 'google'
          ? 'Active maps provider: Google Maps'
          : provider === 'mapbox'
            ? 'Active maps provider: Mapbox'
            : 'Active maps provider: Manual coordinates only',
      );
      reload();
    } catch (err) {
      showError(errorText(err, 'Could not change the maps provider.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="maps-switch-card">
      <div>
        <h2>Active maps provider</h2>
        <p className="text-muted">
          Both Google Maps and Mapbox credentials can be saved. This switch chooses which one
          is used to find addresses on the map across DispatchDash. Zone drawing always uses the
          built-in map.
        </p>
      </div>
      {loading ? (
        <div className="spinner" />
      ) : (
        <div className="maps-switch-options" role="radiogroup" aria-label="Active maps provider">
          {(
            [
              { id: 'google', label: 'Google Maps', hint: 'Address search and map locations by Google' },
              { id: 'mapbox', label: 'Mapbox', hint: 'Address search, map locations and distances by Mapbox' },
              { id: 'manual', label: 'Manual only', hint: 'No address search; enter map locations by hand' },
            ] as const
          ).map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={active === opt.id}
              className={`maps-switch-option ${active === opt.id ? 'selected' : ''}`}
              disabled={saving}
              onClick={() => select(opt.id)}
            >
              <strong>{opt.label}</strong>
              <span>{opt.hint}</span>
            </button>
          ))}
        </div>
      )}
      <FlashMessage flash={flash} />
      <style>{`
        .maps-switch-card {
          background: var(--color-surface);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          padding: var(--space-lg);
          margin-bottom: var(--space-lg);
          display: flex;
          flex-direction: column;
          gap: var(--space-md);
        }
        .maps-switch-card h2 {
          margin: 0 0 var(--space-xs);
          font-size: 1.125rem;
          color: var(--color-forest);
        }
        .maps-switch-options {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: var(--space-sm);
        }
        @media (max-width: 800px) {
          .maps-switch-options { grid-template-columns: 1fr; }
        }
        .maps-switch-option {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          gap: 4px;
          text-align: left;
          padding: var(--space-md);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          background: var(--color-sand);
          cursor: pointer;
        }
        .maps-switch-option:hover:not(:disabled) {
          border-color: var(--color-forest);
        }
        .maps-switch-option.selected {
          background: var(--color-forest);
          color: var(--color-sand);
          border-color: var(--color-forest);
        }
        .maps-switch-option strong { font-size: 0.9375rem; }
        .maps-switch-option span { font-size: 0.75rem; opacity: 0.85; }
      `}</style>
    </section>
  );
}

function XeroRetryQueue() {
  const { data: pushes, loading, error, reload } = useAsync(() => integrationsApi.xeroFailedPushes(), []);
  const [retrying, setRetrying] = useState(false);
  const { flash, ok: showOk, error: showError, clear: clearFlash } = useFlash();

  const pending = (pushes ?? []).filter((p) => p.status === 'pending');

  const retryAll = async () => {
    setRetrying(true);
    clearFlash();
    try {
      const res = await integrationsApi.xeroRetryFailed();
      const text = `Retried ${res.attempted} · sent ${res.resolved} · still failing ${res.stillFailing}`;
      if (res.stillFailing > 0) showError(text);
      else showOk(text);
      reload();
    } catch (err) {
      showError(errorText(err, 'Could not retry the failed invoices.'));
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="retry-block">
      <div className="row-between">
        <h3>Invoices that failed to reach Xero</h3>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          loading={retrying}
          disabled={!pending.length}
          onClick={retryAll}
        >
          Retry all ({pending.length})
        </Button>
      </div>
      <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
        Invoices that couldn't be sent to Xero wait here. Retrying is safe: an invoice is never
        created twice in Xero.
      </p>
      <FlashMessage flash={flash} style={{ fontSize: '0.8125rem' }} />
      <DataTable
        data={pushes ?? []}
        keyField="id"
        loading={loading}
        loadError={error}
        dense
        emptyMessage="Nothing waiting. Every invoice reached Xero."
        columns={[
          { key: 'idempotencyKey', header: 'Reference' },
          {
            key: 'status',
            header: 'Status',
            render: (r) => <StatusBadge status={r.status} size="sm" />,
          },
          { key: 'attempts', header: 'Attempts' },
          { key: 'errorMessage', header: 'Last error', render: (r) => r.errorMessage ?? '—' },
          {
            key: 'lastAttemptAt',
            header: 'Last attempt',
            render: (r) => (r.lastAttemptAt ? formatDateTime(r.lastAttemptAt) : '—'),
          },
        ]}
      />
      <style>{`
        .retry-block {
          margin-top: var(--space-xl);
          padding-top: var(--space-lg);
          border-top: 1px solid var(--color-border);
          display: flex;
          flex-direction: column;
          gap: var(--space-sm);
        }
        .retry-block h3 { font-size: 1rem; }
      `}</style>
    </div>
  );
}

function MapsUsagePanel() {
  const { data: usage, loading, error, reload } = useAsync(() => integrationsApi.mapsUsage(), []);
  const total = (usage ?? []).reduce((sum, u) => sum + u.callCount, 0);

  return (
    <div className="retry-block">
      <div className="row-between">
        <h3>Maps usage this month</h3>
        <Button type="button" size="sm" variant="secondary" onClick={() => reload()}>Refresh</Button>
      </div>
      <p className="text-muted" style={{ fontSize: '0.8125rem' }}>
        How many address searches and map lookups DispatchDash made this month, by type. Your
        maps provider may bill per lookup.
      </p>
      {!loading && usage?.length ? (
        <p className="text-muted" style={{ fontSize: '0.8125rem' }}>Total this month: <strong>{total}</strong></p>
      ) : null}
      <DataTable
        data={usage ?? []}
        keyField="id"
        loading={loading}
        loadError={error}
        dense
        emptyMessage="No maps API calls recorded yet this month."
        columns={[
          { key: 'sku', header: 'Lookup type', render: (r) => humanize(r.sku) },
          { key: 'month', header: 'Month' },
          { key: 'callCount', header: 'Calls' },
        ]}
      />
    </div>
  );
}

function StorageWhere({ status }: { status?: IntegrationStatus }) {
  const st = status?.storage;
  if (!st) return null;
  if (st.mode === 'local') {
    return (
      <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: 0 }}>
        Files are currently saved on this server.{st.note ? ` ${st.note.replace(/ — using local disk$/, '.')}` : ''}
      </p>
    );
  }
  return (
    <p className="text-muted" style={{ fontSize: '0.875rem', marginTop: 0 }}>
      Files are saved to bucket <strong>{st.bucket}</strong>
      {st.provider === 'r2' ? ' on Cloudflare R2' : st.endpointHost ? ` at ${st.endpointHost}` : ''}
      {st.source === 'env' ? ' (set in the server environment)' : ''}.
    </p>
  );
}

/** Firebase service-account key (paste or upload) + "Send test" to the admin's own phones. */
function FirebasePanel({ status, onSaved }: { status?: IntegrationStatus; onSaved: () => void }) {
  const [json, setJson] = useState('');
  const [fileName, setFileName] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const { flash, ok: showOk, error: showError, warn: showWarn, clear: clearFlash } = useFlash();
  const toast = useToast();

  const onFile = async (file: File | undefined) => {
    clearFlash();
    if (!file) return;
    if (file.size > 100_000) {
      showError('That file is too big to be a service account key.');
      return;
    }
    setFileName(file.name);
    setJson(await file.text());
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    clearFlash();
    if (!json.trim()) {
      showError('Upload or paste the service account key file first.');
      return;
    }
    setSaving(true);
    try {
      await integrationsApi.saveCredentials('firebase', { serviceAccountJson: json });
      setJson('');
      setFileName('');
      showOk('Push notification settings saved securely. Use "Send test" to check it works.');
      toast('Firebase key saved');
      onSaved();
    } catch (err) {
      showError(errorText(err, 'Could not save the Firebase key.'));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    clearFlash();
    setTesting(true);
    try {
      const res = await integrationsApi.testPush();
      if (res.success) showOk(res.message);
      else if (!res.configured || res.devices === 0) showWarn(res.message);
      else showError(res.message);
      onSaved();
    } catch (err) {
      showError(errorText(err, 'The test notification could not be sent.'));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="cred-form">
      {status?.firebase?.projectId && (
        <p className="text-muted" style={{ fontSize: '0.875rem', margin: 0 }}>
          Connected to Firebase project <strong>{status.firebase.projectId}</strong>.
        </p>
      )}
      <form className="cred-form" onSubmit={save}>
        <label className="fb-file">
          <span>Service account key file</span>
          <input type="file" accept="application/json,.json" onChange={(e) => void onFile(e.target.files?.[0])} />
          {fileName && <span className="text-muted">Loaded {fileName}</span>}
        </label>
        <label className="fb-paste">
          <span>…or paste the key file contents</span>
          <textarea
            rows={6}
            value={json}
            onChange={(e) => {
              setJson(e.target.value);
              setFileName('');
            }}
            placeholder='{ "type": "service_account", "project_id": "…", … }'
            spellCheck={false}
            autoComplete="off"
          />
        </label>
        <div className="row">
          <Button type="submit" loading={saving}>Save key</Button>
          <Button type="button" variant="secondary" loading={testing} onClick={sendTest} disabled={!status?.configured}>
            Send test
          </Button>
        </div>
        <p className="text-muted" style={{ fontSize: '0.8125rem', margin: 0 }}>
          “Send test” sends a notification to the phones where you are signed in to the crew app.
        </p>
      </form>
      <FlashMessage flash={flash} />
      <style>{`
        .fb-file, .fb-paste { display: flex; flex-direction: column; gap: 4px; font-size: 0.875rem; font-weight: 500; }
        .fb-paste textarea {
          font-family: var(--font-mono, monospace);
          font-size: 0.75rem;
          padding: var(--space-sm);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          resize: vertical;
        }
      `}</style>
    </div>
  );
}
